import { OverlayViewF, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import FannedPin from './FannedPin';
import { CAT_META, makePinIcon } from '../../utils/mapConstants';
import type { PinOffset } from '../../utils/pinCollision';
import type { PinnedVenue } from './VenueDetailCard';
import type { RouteStop } from '../../utils/routeGroups';

/**
 * The three kinds of pin a trip map draws. Shared by the trip's own map and the
 * community one, so a place looks the same whichever page it is seen from.
 */

interface VenuePinProps {
  venue: PinnedVenue;
  fan: PinOffset | undefined;
  active: boolean;
  onClick: () => void;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
}

/** A saved place, in the places view: category-coloured drop pin. */
export function VenuePin({ venue: v, fan, active, onClick, onMouseOver, onMouseOut }: VenuePinProps) {
  const color = CAT_META[v.category].markerColor;
  const pin = makePinIcon(color, v.visited);
  return (
    <OverlayViewF position={{ lat: v.lat, lng: v.lng }}
      mapPaneName={OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: -12, y: -32 })}
      zIndex={active ? 900 : undefined}>
      <FannedPin offset={fan} anchor={{ x: 12, y: 32 }} color={color}>
        <img src={pin.url} width={24} height={32} alt="" draggable={false}
          title={onMouseOver ? undefined : v.name}
          style={{ cursor: 'pointer', userSelect: 'none', display: 'block' }}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          onClick={e => { e.stopPropagation(); onClick(); }}
        />
      </FannedPin>
    </OverlayViewF>
  );
}

interface SavedPlaceDotProps {
  venue: PinnedVenue;
  fan: PinOffset | undefined;
  onClick: () => void;
}

/** A saved place that isn't on any route, in the routes view: a faint dot. */
export function SavedPlaceDot({ venue: v, fan, onClick }: SavedPlaceDotProps) {
  const color = CAT_META[v.category].markerColor;
  return (
    <OverlayViewF position={{ lat: v.lat, lng: v.lng }}
      mapPaneName={OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: -9, y: -9 })}>
      <FannedPin offset={fan} anchor={{ x: 9, y: 9 }} color={color}>
        <div
          title={v.name}
          onClick={e => { e.stopPropagation(); onClick(); }}
          style={{
            width: 18, height: 18, borderRadius: '50%',
            backgroundColor: color,
            border: '2px solid white', opacity: 0.75,
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'pointer',
          }}
        />
      </FannedPin>
    </OverlayViewF>
  );
}

interface RouteStopPinProps {
  stop: RouteStop & { position: NonNullable<RouteStop['position']> };
  color: string;
  fan: PinOffset | undefined;
  /** Overrides the day's own number when several days share the map. */
  num?: number;
  active: boolean;
  onClick: () => void;
}

/** A stop on a day's route: numbered within its day (or the shown days), coloured by it. */
export function RouteStopPin({ stop, color, fan, num, active, onClick }: RouteStopPinProps) {
  return (
    <OverlayViewF position={stop.position}
      mapPaneName={OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: -12, y: -12 })}
      zIndex={active ? 900 : undefined}>
      <FannedPin offset={fan} anchor={{ x: 12, y: 12 }} color={color}>
        <div
          title={stop.activity.venueName ?? stop.activity.title}
          onClick={e => { e.stopPropagation(); onClick(); }}
          style={{
            // Two or three digits (several days count on) widen the circle into a pill.
            minWidth: 24, height: 24, borderRadius: 12, padding: '0 4px', boxSizing: 'border-box', backgroundColor: color,
            border: active ? '2.5px solid white' : '2px solid white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: 10, fontWeight: 'bold', userSelect: 'none',
            boxShadow: '0 1px 4px rgba(0,0,0,0.35)', cursor: 'pointer',
            transform: active ? 'scale(1.15)' : 'none', transition: 'transform 0.1s',
          }}
        >
          {num ?? stop.num}
        </div>
      </FannedPin>
    </OverlayViewF>
  );
}
